<template>
    <div class="open-diagnoses">
        <div class="panel-heading">
            <h3>Open Diagnoses</h3>
            <div v-if="!isLoading" class="add">
                <img
                    v-if="isLoadingModal"
                    class="spinner"
                    src="/apps/assets/img/ajax-spinner.gif"
                />

                <b-button
                    size="sm"
                    v-else
                    class="btn plus"
                    :disabled="skipFetchData"
                    @click="openModalDiagnosis"
                >
                    &#43;
                </b-button>
            </div>
        </div>

        <loader v-if="isLoading" />

        <div v-else>
            <div v-if="items.length > 0">
                <div class="subheader">
                    <span>Diagnosis:</span>
                    <span>Acuity:</span>
                    <span>Opened:</span>
                </div>

                <div
                    v-for="item in items"
                    v-bind:key="item.id"
                    class="item-row"
                >
                    <input type="hidden" name="id" :value="item.id" />
                    <span>{{ item.description }}</span>
                    <span>{{ item.is_acute ? "Acute" : "Chronic" }}</span>
                    <span>{{ properDate(item.date_opened) }}</span>

                    <div v-if="loadingPermissions" class="options-loading">
                        <img src="/apps/assets/img/ajax-spinner.gif" />
                    </div>

                    <b-dropdown
                        id="dropdown-right"
                        right
                        size="sm"
                        no-caret
                        toggle-class="options-toggle"
                        lazy
                    >
                        <template slot="button-content">
                            <span class="more">&#183;&#183;&#183;</span>
                        </template>

                        <b-dropdown-item
                            @click.prevent="closeDiagnosis(item)"
                            v-if="_hasAccess('patient.record.update')"
                        >
                            <i class="cus-accept"></i> Close Diagnosis
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="_hasAccess('patient.record.update')"
                            @click.prevent="
                                openModalDiagnosis({
                                    action: 'edit',
                                    record: item
                                })
                            "
                        >
                            <i class="cus-page-white-edit"></i> Edit Diagnosis
                        </b-dropdown-item>

                        <b-dropdown-item
                            @click.prevent="deleteDiagnosis(item)"
                            v-if="_hasAccess('patient.record.delete')"
                        >
                            <i class="cus-delete"></i> Delete Diagnosis
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
            <div v-else>
                <div class="subheader">
                    <span>Diagnosis:</span>
                    <span>Acuity:</span>
                    <span>Opened:</span>
                </div>

                <p style="margin: 5px 0;">No records available.</p>
            </div>
        </div>

        <modal-diagnosis
            ref="modalDiagnosis"
            @onsuccess="onSuccess"
            :patientId="patientId"
        />
        <modal-close-diagnosis
            ref="modalCloseDiagnosis"
            @onsuccess="onSuccess"
            :patientId="patientId"
        />
        <modal-delete
            ref="modalDelete"
            title="Delete Diagnosis"
            module="diagnosis"
            :patientId="patientId"
            @onsuccess="onSuccess"
        >
            Are you sure you want to permanently delete this record?
        </modal-delete>
    </div>
</template>

<script>
import axios from "axios";
import { DateTime } from "luxon";
import ModalDiagnosis from "@/components/commons/modal-diagnosis";
import ModalCloseDiagnosis from "@/components/commons/modal-close-diagnosis";
import ModalDelete from "./modal-delete";

export default {
    components: {
        ModalDiagnosis,
        ModalCloseDiagnosis,
        ModalDelete,
        Loader: () => import("@/components/elements/loader-list")
    },
    data: function () {
        return {
            isLoading: true,
            items: [],
            itemToBeDeleted: null,
            isLoadingModal: false
        };
    },
    props: {
        permissions: {
            type: Object,
            default() {
                return {};
            }
        },
        loadingPermissions: {
            type: Boolean,
            default: true
        },
        isSuperUser: {
            type: Boolean,
            default: false
        },
        currentBusinessId: {
            type: String
        },
        skipFetchData: {
            type: Boolean,
            default: false
        },
        patientId: {
            type: String,
            default: null,
            required: true
        }
    },
    mounted: function () {
        if (this.skipFetchData) {
            this.isLoading = false;
        } else {
            this.fetchData();
        }
    },
    methods: {
        openModalDiagnosis(params = {}) {
            this.$refs.modalDiagnosis.open({
                action: params.action || "add",
                editRecord: params.record
            });
        },
        properDate(date = "") {
            return DateTime.fromISO(date)
                .setZone(this._timeZone)
                .toFormat(this._getLuxonFormat());
        },
        onSuccess() {
            this.$store.dispatch("patient/getHistoryCount", {
                patientId: this.patientId
            });
            this.fetchData();
        },
        fetchData: async function () {
            this.isLoading = true;

            const res = await axios.get(
                process.env.RECORD_SERVICE + "diagnosis/" + this.patientId,
                {
                    params: {
                        status: "opened"
                    }
                }
            );

            this.isLoading = false;

            if (res.data.response.message.code === 200) {
                this.items = res.data.response.resources;
            }
        },
        closeDiagnosis(item) {
            this.$refs.modalCloseDiagnosis.open(item);
        },
        deleteDiagnosis(item) {
            this.$refs.modalDelete.open(item.id);
        }
    }
};
</script>

<style scoped>
.panel-heading {
    display: grid;
    grid-template-columns: 1fr 28px;
    background-color: transparent;
    align-items: center;
    margin-bottom: 10px;
}
.panel-heading h3 {
    margin: 0;
    font-size: 1.3em;
}
.panel-heading .add {
    text-align: center;
}
.panel-heading .add .spinner {
    opacity: 0.5;
}
.subheader {
    display: grid;
    grid-template-columns: 1fr 120px 120px 28px;
}
.subheader span {
    font-weight: bold;
}
.item-row {
    display: grid;
    grid-template-columns: 1fr 120px 120px 28px;
    border-bottom: 1px solid #ececec;
    padding: 10px 0;
    align-items: flex-start;
}
.item-row .btn.more {
    color: #333;
    padding: 2px 0;
}
.item-row ul.nav-pills li a {
    padding: 5px 0;
}
.options-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.4;
}
a.btn.plus,
a.btn.more {
    border-radius: 0;
}
.open-diagnoses {
    margin-bottom: 50px;
}
.btn.plus {
    border-radius: 0;
    font-weight: bold;
    font-size: 1.1em;
}
.more {
    font-weight: bold;
    font-size: 1.1em;
}
::v-deep .options-toggle {
    padding: 0;
    border-radius: 0 !important;
}
</style>
